import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ImportExportApiService } from 'app/apiService';
import type { 
  ImportExportEntity, 
  EntityField, 
  EntityFieldsResponse,
  ImportExportEntitiesResponse,
  CompareEntitiesResponse 
} from 'app/apiService/importExport/importExportApiService';
import { APIResponseType } from 'app/apiService/types';

// Import/Export Context State Interface
export interface ImportExportState {
  // Entities
  entities: ImportExportEntity[];
  entitiesLoading: boolean;
  entitiesError: string | null;

  // Entity Fields
  entityFields: Record<string, EntityField[]>;
  fieldsLoading: boolean;
  fieldsError: string | null;

  // Import/Export Operations
  isImporting: boolean;
  isExporting: boolean;
  importProgress: number;
  exportProgress: number;

  // Compare Entities
  isComparing: boolean;
  compareResults: CompareEntitiesResponse | null;
  compareError: string | null;
}

// Import/Export Context Actions Interface
export interface ImportExportActions {
  // Entity Methods
  fetchEntities: (params?: { entity_type?: string; search?: string; limit?: number; skip?: number }) => Promise<void>;
  fetchEntityFields: (entityType: string) => Promise<EntityField[]>;
  
  // Import/Export Methods
  exportData: (entityType: string, params?: {
    format?: 'zip' | 'csv' | 'json';
    limit?: number;
    filters?: string;
    fields?: string[];
    dealer_id?: string;
  }) => Promise<any>;
  
  importData: (entityType: string, formData: FormData, params?: {
    dealer_id?: string;
    validate_only?: boolean;
    update_existing?: boolean;
  }) => Promise<any>;
  
  importDataFromJson: (entityType: string, jsonData: any[], params?: {
    dealer_id?: string;
    validate_only?: boolean;
    update_existing?: boolean;
  }) => Promise<any>;
  
  // Compare Entities Method
  compareEntities: (entityType: string, formData: FormData, params?: {
    dealer_id?: string;
  }) => Promise<CompareEntitiesResponse>;
  
  // Utility Methods
  clearErrors: () => void;
  resetState: () => void;
}

// Combined Context Type
export interface ImportExportContextType extends ImportExportState, ImportExportActions {}

// Initial State
const initialState: ImportExportState = {
  entities: [],
  entitiesLoading: false,
  entitiesError: null,
  
  entityFields: {},
  fieldsLoading: false,
  fieldsError: null,
  
  isImporting: false,
  isExporting: false,
  importProgress: 0,
  exportProgress: 0,
  
  isComparing: false,
  compareResults: null,
  compareError: null,
};

// Create Context
const ImportExportContext = createContext<ImportExportContextType | undefined>(undefined);

// Custom Hook
export const useImportExport = (): ImportExportContextType => {
  const context = useContext(ImportExportContext);
  if (!context) {
    throw new Error('useImportExport must be used within an ImportExportProvider');
  }
  return context;
};

// Provider Props
interface ImportExportProviderProps {
  children: React.ReactNode;
}

// Provider Component
export const ImportExportProvider: React.FC<ImportExportProviderProps> = ({ children }) => {
  const [state, setState] = useState<ImportExportState>(initialState);

  // Helper function to update state
  const updateState = useCallback((updates: Partial<ImportExportState>) => {
    setState(prevState => ({ ...prevState, ...updates }));
  }, []);

  // Clear all errors
  const clearErrors = useCallback(() => {
    updateState({
      entitiesError: null,
      fieldsError: null,
      compareError: null,
    });
  }, [updateState]);

  // Reset entire state
  const resetState = useCallback(() => {
    setState(initialState);
  }, []);

  // Fetch available entities
  const fetchEntities = useCallback(async (params?: { 
    entity_type?: string; 
    search?: string; 
    limit?: number; 
    skip?: number 
  }) => {
    try {
      updateState({ entitiesLoading: true, entitiesError: null });
      
      console.log('🔍 Fetching entities with params:', params);
      const response: APIResponseType<ImportExportEntitiesResponse> = await ImportExportApiService.getEntities(params);
      console.log('📦 Full API Response:', response);
      
      let entities: ImportExportEntity[] = [];
      
      // Handle different response structures based on API response
      if (response.body) {
        if (Array.isArray(response.body)) {
          // Direct array in body - convert strings to proper objects
          entities = response.body.map((item: any) => {
            if (typeof item === 'string') {
              return { name: item, display_name: item.charAt(0).toUpperCase() + item.slice(1) };
            }
            return item;
          });
        } else if (response.body.body && Array.isArray(response.body.body)) {
          // Nested body structure
          entities = response.body.body.map((item: any) => {
            if (typeof item === 'string') {
              return { name: item, display_name: item.charAt(0).toUpperCase() + item.slice(1) };
            }
            return item;
          });
        } else if (response.body.data && Array.isArray(response.body.data)) {
          // Data array structure
          entities = response.body.data.map((item: any) => {
            if (typeof item === 'string') {
              return { name: item, display_name: item.charAt(0).toUpperCase() + item.slice(1) };
            }
            return item;
          });
        } else {
          console.warn('🔥 Unexpected response structure:', response.body);
          // Fallback - try to extract from any array property
          const bodyKeys = Object.keys(response.body);
          for (const key of bodyKeys) {
            if (Array.isArray(response.body[key])) {
              entities = response.body[key].map((item: any) => {
                if (typeof item === 'string') {
                  return { name: item, display_name: item.charAt(0).toUpperCase() + item.slice(1) };
                }
                return item;
              });
              break;
            }
          }
        }
      }
      
      console.log('✅ Processed entities:', entities);
      
      if (entities.length > 0) {
        updateState({ 
          entities: entities,
          entitiesLoading: false 
        });
      } else {
        console.warn('⚠️ No entities found in response');
        // Still update state but with empty array
        updateState({ 
          entities: [],
          entitiesLoading: false,
          entitiesError: 'No entities found'
        });
      }
    } catch (error: any) {
      console.error('❌ Error fetching entities:', error);
      updateState({ 
        entitiesError: error?.message || 'Failed to fetch entities',
        entitiesLoading: false 
      });
    }
  }, [updateState]);

  // Fetch entity fields
  const fetchEntityFields = useCallback(async (entityType: string): Promise<EntityField[]> => {
    try {
      updateState({ fieldsLoading: true, fieldsError: null });
      
      const response: APIResponseType<EntityFieldsResponse> = await ImportExportApiService.getEntityFields({ 
        entity_type: entityType 
      });
      
      if (response.body && response.body.fields) {
        const fields = response.body.fields;
        updateState({ 
          entityFields: { 
            ...state.entityFields, 
            [entityType]: fields 
          },
          fieldsLoading: false 
        });
        return fields;
      } else {
        throw new Error('Invalid entity fields response format');
      }
    } catch (error: any) {
      console.error('Error fetching entity fields:', error);
      updateState({ 
        fieldsError: error?.message || 'Failed to fetch entity fields',
        fieldsLoading: false 
      });
      return [];
    }
  }, [updateState, state.entityFields]);

  // Export data
  const exportData = useCallback(async (entityType: string, params?: {
    format?: 'zip' | 'csv' | 'json';
    limit?: number;
    filters?: string;
    fields?: string[];
    dealer_id?: string;
  }) => {
    try {
      console.log('🔄 Context exportData called:', { entityType, params });
      updateState({ isExporting: true, exportProgress: 0 });
      
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setState(prevState => ({ 
          ...prevState,
          exportProgress: Math.min(prevState.exportProgress + 10, 90) 
        }));
      }, 200);
      
      console.log('📡 Calling ImportExportApiService.exportData...');
      const response = await ImportExportApiService.exportData(entityType, params);
      console.log('📨 API Service response:', response);
      
      clearInterval(progressInterval);
      updateState({ 
        isExporting: false, 
        exportProgress: 100 
      });
      
      // Reset progress after a short delay
      setTimeout(() => {
        updateState({ exportProgress: 0 });
      }, 1000);
      
      return response;
    } catch (error: any) {
      console.error('❌ Context exportData error:', error);
      updateState({ 
        isExporting: false, 
        exportProgress: 0 
      });
      throw error;
    }
  }, [updateState]);

  // Import data
  const importData = useCallback(async (entityType: string, formData: FormData, params?: {
    dealer_id?: string;
    validate_only?: boolean;
    update_existing?: boolean;
  }) => {
    try {
      updateState({ isImporting: true, importProgress: 0 });
      
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setState(prevState => ({ 
          ...prevState,
          importProgress: Math.min(prevState.importProgress + 10, 90) 
        }));
      }, 300);
      
      const response = await ImportExportApiService.importData(entityType, formData, params);
      
      clearInterval(progressInterval);
      updateState({ 
        isImporting: false, 
        importProgress: 100 
      });
      
      // Reset progress after a short delay
      setTimeout(() => {
        updateState({ importProgress: 0 });
      }, 1000);
      
      return response;
    } catch (error: any) {
      console.error('Error importing data:', error);
      updateState({ 
        isImporting: false, 
        importProgress: 0 
      });
      throw error;
    }
  }, [updateState]);

  // Import data from JSON payload
  const importDataFromJson = useCallback(async (entityType: string, jsonData: any[], params?: {
    dealer_id?: string;
    validate_only?: boolean;
    update_existing?: boolean;
  }) => {
    try {
      console.log('🔄 Context importDataFromJson called:', { entityType, params, dataCount: jsonData.length });
      updateState({ isImporting: true, importProgress: 0 });
      
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setState(prevState => ({ 
          ...prevState,
          importProgress: Math.min(prevState.importProgress + 10, 90) 
        }));
      }, 300);
      
      const response = await ImportExportApiService.importDataFromJson(entityType, jsonData, params);
      
      clearInterval(progressInterval);
      updateState({ 
        isImporting: false, 
        importProgress: 100 
      });
      
      // Reset progress after a short delay
      setTimeout(() => {
        updateState({ importProgress: 0 });
      }, 1000);
      
      console.log('✅ Context importDataFromJson successful:', response);
      return response;
    } catch (error: any) {
      console.error('❌ Context importDataFromJson error:', error);
      updateState({ 
        isImporting: false, 
        importProgress: 0 
      });
      throw error;
    }
  }, [updateState]);

  // Compare entities
  const compareEntities = useCallback(async (entityType: string, formData: FormData, params?: {
    dealer_id?: string;
  }): Promise<CompareEntitiesResponse> => {
    try {
      updateState({ 
        isComparing: true, 
        compareError: null,
        compareResults: null 
      });
      
      const response = await ImportExportApiService.compareEntities(entityType, formData, params);
      
      console.log('🔍 Context received response:', response);
      
      if (response.body) {
        // Handle the response structure - the API returns the array directly in body
        const results = Array.isArray(response.body) ? response.body : response.body.body || response.body;
        
        updateState({ 
          isComparing: false,
          compareResults: results
        });
        
        console.log('🔍 Context setting compareResults:', results);
        
        return results;
      } else {
        throw new Error('Invalid compare entities response format');
      }
    } catch (error: any) {
      updateState({ 
        isComparing: false,
        compareError: error?.message || 'Failed to compare entities'
      });
      throw error;
    }
  }, [updateState]);


  // Context value
  const contextValue: ImportExportContextType = {
    // State
    ...state,
    
    // Actions
    fetchEntities,
    fetchEntityFields,
    exportData,
    importData,
    importDataFromJson,
    compareEntities,
    clearErrors,
    resetState,
  };

  return (
    <ImportExportContext.Provider value={contextValue}>
      {children}
    </ImportExportContext.Provider>
  );
};

export default ImportExportContext;
